/** Chase movement logic: skill selection, direct pursuit, pathfinding to LKP, exit prediction, and search transitions. */
import type { EnemyArchetypeConfig } from '../utils/Types';
import type { PathHelper } from './PathHelper';
import type { Detection } from './Detection';
import type { Aggro } from './Aggro';
import type { SearchSystem } from './SearchSystem';
import type { EnemySkillRegistry, SkillSelectionContext } from '../skills/enemy/EnemySkillRegistry';
import { EnemyDashSkillEffect } from '../skills/enemy/EnemyDashSkillEffect';
import { EnemyRangedSkillEffect } from '../skills/enemy/EnemyRangedSkillEffect';
import { EnemyAreaSkillEffect } from '../skills/enemy/EnemyAreaSkillEffect';
import { EnemyBlockSkillEffect } from '../skills/enemy/EnemyBlockSkillEffect';
import { EnemyBuffSkillEffect } from '../skills/enemy/EnemyBuffSkillEffect';
import { EnemyBackupSkillEffect } from '../skills/enemy/EnemyBackupSkillEffect';
import { lerp, distance, scaledNormalize } from '../utils/Math';
import type { Position } from '../utils/Types';
import type { AIState, MovementResult } from './constants';
import { ZERO_MOVEMENT, STUCK_ESCALATION_INTERVAL_MS, DUTY_STUCK_PROGRESS, scaledAStarBudget, ESCALATED_A_STAR_BUDGET, PATH_WAYPOINT_THRESHOLD } from './constants';
import { StuckDetector } from './StuckDetector';

/** Snapshot of mutable state that ChaseSystem reads but does not own. */
export interface ChaseContext {
  entityX: number;
  entityY: number;
  dx: number;
  dy: number;
  distToPlayer: number;
  delta: number;
  hasLOS: boolean;
  hasLastKnown: boolean;
  lastKnownX: number;
  lastKnownY: number;
  recentlyDamaged: boolean;
  sprintMultiplier: number;
  facingX: number;
  facingY: number;
  playerDirX: number;
  playerDirY: number;
  countNearbyAllies: ((x: number, y: number, radius: number) => number) | null;
}

/** State mutations the caller should apply after processing a ChaseResult. */
export interface ChaseStateUpdate {
  /** New AI state to transition into. */
  newState?: AIState;
  /** Updated last-known-X coordinate. */
  lastKnownX?: number;
  /** Updated last-known-Y coordinate. */
  lastKnownY?: number;
  /** Whether a valid LKP exists. Explicit false clears it. */
  hasLastKnown?: boolean;
  /** If true, the path should be cleared. */
  clearPath?: boolean;
  /** If true, search state should be initialized from the current position. */
  beginSearch?: boolean;
}

/** The complete result of a chase tick. */
export interface ChaseResult {
  movement: MovementResult;
  stateUpdate: ChaseStateUpdate;
  logs: string[];
}

/**
 * Pure-logic chase subsystem: skill selection, direct pursuit, pathfinding
 * toward last-known position, exit prediction chain, and search transitions.
 *
 * Owns no mutable AI state — receives everything it needs through ChaseContext,
 * returns mutation instructions via ChaseStateUpdate.
 */
export class ChaseSystem {
  private readonly config: EnemyArchetypeConfig;
  private readonly pathHelper: PathHelper;
  private readonly detection: Detection;
  private readonly aggroSystem: Aggro;
  private readonly searchSystem: SearchSystem;
  private readonly skillRegistry: EnemySkillRegistry;

  // Chase stuck detection — four-stage escalation ladder:
  //   1. Bump A* budget to 2000 nodes
  //   2. Try reverse pathfinding (T→A reversed)
  //   3. Nudge to adjacent non-buffer tile
  //   4. Force arrival (terminal)
  // Uses movement-based detection: only counts time as stuck when the entity is
  // physically not moving. Circuitous detour paths that temporarily increase
  // distance to LKP are NOT stuck — the entity is following a valid route.
  private readonly stuckDetector = new StuckDetector(STUCK_ESCALATION_INTERVAL_MS, {
    progressThresholdPx: DUTY_STUCK_PROGRESS,
    persistEscalation: false,
    requireMovementStall: true,
    maxEscalation: 4,
  });

  // Track the LKP target across ticks to detect target changes and reset stuck state.
  private prevLKPX = NaN;
  private prevLKPY = NaN;

  // Track entity position across ticks for stuck-detection log messages.
  private prevEntityX = NaN;
  private prevEntityY = NaN;

  // Active nudge target — when set, the entity moves toward this tile center
  // instead of following A* pathfinding. Cleared on arrival or LOS reacquisition.
  private nudgeTarget: Position | null = null;

  // LOS stall detection — tracks physical displacement over a rolling window.
  // When the LOS branch fires raw velocity but a wall blocks movement, the
  // enemy freezes because stuckDetector.reset() is called every frame. These
  // fields detect that stall and trigger an A* fallback without touching the
  // stuck detector's escalation ladder (which is for genuine pathfinding failures).
  private losWindowStartX = NaN;
  private losWindowStartY = NaN;
  private losWindowStartTime = 0;
  private losStalled = false;
  private static readonly LOS_STALL_WINDOW_MS = 200;
  private static readonly LOS_STALL_THRESHOLD_PX = 4;

  constructor(
    config: EnemyArchetypeConfig,
    pathHelper: PathHelper,
    detection: Detection,
    aggroSystem: Aggro,
    searchSystem: SearchSystem,
    skillRegistry: EnemySkillRegistry,
  ) {
    this.config = config;
    this.pathHelper = pathHelper;
    this.detection = detection;
    this.aggroSystem = aggroSystem;
    this.searchSystem = searchSystem;
    this.skillRegistry = skillRegistry;
  }

  /** Run chase-state logic: attempt skill use, then direct pursue or pathfind toward LKP. */
  getMovement(ctx: ChaseContext): ChaseResult {
    const logs: string[] = [];
    const stateUpdate: ChaseStateUpdate = {};

    // --- Multi-skill selection (ADR-033) ---
    const selectionContext: SkillSelectionContext = {
      distToPlayer: ctx.distToPlayer,
      hasLOS: ctx.hasLOS,
      recentlyDamaged: ctx.recentlyDamaged,
      alliesInRange: ctx.countNearbyAllies
        ? ctx.countNearbyAllies(ctx.entityX, ctx.entityY, this.config.buffRadius ?? 128)
        : 0,
    };
    const skillId = this.skillRegistry.getAvailableSkill(selectionContext);
    if (skillId) {
      const skill = this.skillRegistry.getSkill(skillId);
      if (skill instanceof EnemyDashSkillEffect) {
        skill.setTarget(ctx.entityX, ctx.entityY, ctx.lastKnownX, ctx.lastKnownY);
      } else if (skill instanceof EnemyRangedSkillEffect) {
        skill.setTarget(ctx.entityX, ctx.entityY, ctx.lastKnownX, ctx.lastKnownY);
      } else if (skill instanceof EnemyAreaSkillEffect) {
        skill.setCenter(ctx.entityX, ctx.entityY);
      } else if (skill instanceof EnemyBlockSkillEffect) {
        skill.setFacing(ctx.facingX, ctx.facingY);
      } else if (skill instanceof EnemyBuffSkillEffect) {
        skill.setOrigin(ctx.entityX, ctx.entityY);
      } else if (skill instanceof EnemyBackupSkillEffect) {
        skill.setOrigin(ctx.entityX, ctx.entityY);
      }
      if (this.skillRegistry.requestSkill(skillId)) {
        logs.push(`SKILL range=${Math.round(ctx.distToPlayer)} → using_skill(${skillId})`);
        stateUpdate.newState = 'using_skill';
        stateUpdate.clearPath = true;
        return { movement: ZERO_MOVEMENT, stateUpdate, logs };
      }
    }

    // --- Chase speed scales with HWM ---
    const hwmRatio = this.aggroSystem.aggroHWM / 100;
    const chaseSpeed = lerp(this.config.moveSpeed, this.config.chaseSpeed, hwmRatio);

    // --- Direct movement when LOS ---
    if (ctx.hasLOS) {
      // At engagement range, stop and hold — no stall detection needed.
      if (ctx.distToPlayer < 4) {
        this.pathHelper.clearPath();
        this.stuckDetector.reset();
        this.nudgeTarget = null;
        // Reset stall tracker so it doesn't carry over stale displacement
        this.losWindowStartX = NaN;
        this.losWindowStartY = NaN;
        this.losWindowStartTime = 0;
        this.losStalled = false;
        return { movement: ZERO_MOVEMENT, stateUpdate, logs };
      }

      // LOS stall detection: track displacement over a rolling window.
      // If the enemy hasn't moved despite having LOS, a wall is blocking the
      // direct vector — fall through to A* instead of freezing at the corner.
      const now = this.losWindowStartTime + ctx.delta;
      if (Number.isNaN(this.losWindowStartX)) {
        // First LOS frame — start the window
        this.losWindowStartX = ctx.entityX;
        this.losWindowStartY = ctx.entityY;
        this.losWindowStartTime = 0;
        this.losStalled = false;
      } else {
        this.losWindowStartTime = now;
        if (now >= ChaseSystem.LOS_STALL_WINDOW_MS) {
          const disp = distance(
            ctx.entityX, ctx.entityY,
            this.losWindowStartX, this.losWindowStartY,
          );
          if (disp < ChaseSystem.LOS_STALL_THRESHOLD_PX) {
            // Stalled — fall through to A* pathfinding below
            if (!this.losStalled) {
              logs.push(`CHASE LOS stall detected: ${disp.toFixed(1)}px in ${now.toFixed(0)}ms — falling back to A*`);
            }
            this.losStalled = true;
          } else {
            // Moving fine — reset the window
            this.losStalled = false;
          }
          // Reset window for next measurement
          this.losWindowStartX = ctx.entityX;
          this.losWindowStartY = ctx.entityY;
          this.losWindowStartTime = 0;
        }
      }

      if (!this.losStalled) {
        this.pathHelper.clearPath();
        this.stuckDetector.reset();
        this.nudgeTarget = null;
        const speed = chaseSpeed * ctx.sprintMultiplier;
        const vel = scaledNormalize(ctx.dx, ctx.dy, speed);
        return {
          movement: { vx: vel.x, vy: vel.y, wantsAttack: false },
          stateUpdate,
          logs,
        };
      }
      // losStalled=true: do NOT clear path, do NOT reset stuck detector.
      // Fall through to the A* pathfinding section below.
    } else {
      // Not in LOS — reset the LOS stall tracker so a fresh window starts
      // next time LOS is reacquired.
      this.losWindowStartX = NaN;
      this.losWindowStartY = NaN;
      this.losWindowStartTime = 0;
      this.losStalled = false;
    }

    // --- No LOS — pathfind to last known position ---
    if (!ctx.hasLastKnown) {
      // Defensive: should be unreachable — all chase entry paths set LKP.
      // If this fires, investigate the state transition that cleared LKP without leaving chase.
      logs.push('chase: no LKP — standing still (UNEXPECTED: investigate entry path)');
      return { movement: ZERO_MOVEMENT, stateUpdate, logs };
    }

    const distToLKP = distance(ctx.entityX, ctx.entityY, ctx.lastKnownX, ctx.lastKnownY);

    // Reset stuck tracking when LKP target changes (new exit prediction leg, etc.)
    if (ctx.lastKnownX !== this.prevLKPX || ctx.lastKnownY !== this.prevLKPY) {
      if (!Number.isNaN(this.prevLKPX)) {
        logs.push(`CHASE LKP changed (${Math.round(this.prevLKPX)},${Math.round(this.prevLKPY)}) → (${Math.round(ctx.lastKnownX)},${Math.round(ctx.lastKnownY)}) dist=${Math.round(distToLKP)}`);
      }
      this.prevLKPX = ctx.lastKnownX;
      this.prevLKPY = ctx.lastKnownY;
      this.nudgeTarget = null;
      this.stuckDetector.resetProgress();
    }

    // --- Active nudge: move toward adjacent tile instead of A* ---
    if (this.nudgeTarget) {
      const distToNudge = distance(ctx.entityX, ctx.entityY, this.nudgeTarget.x, this.nudgeTarget.y);
      if (distToNudge < PATH_WAYPOINT_THRESHOLD) {
        // Arrived at nudge target — clear and let A* take over from new position
        logs.push(`CHASE NUDGE arrived at (${Math.round(this.nudgeTarget.x)},${Math.round(this.nudgeTarget.y)})`);
        this.nudgeTarget = null;
        this.pathHelper.clearPath();
        // Fall through to normal A* pathfinding below
      } else {
        // Still moving toward nudge target — run stuck detection in case
        // the nudge direction is also physically blocked
        const stuckResult = this.stuckDetector.update(
          ctx.delta, ctx.entityX, ctx.entityY, distToLKP, !!stateUpdate.newState,
        );
        if (stuckResult.triggered >= this.stuckDetector.escalationLevel) {
          // Terminal fired while nudging — give up on nudge, force arrival
          logs.push(`CHASE STUCK: nudge blocked — forcing arrival`);
          this.nudgeTarget = null;
          this.onChaseArrival(ctx, stateUpdate, logs);
          return { movement: ZERO_MOVEMENT, stateUpdate, logs };
        }

        const ndx = this.nudgeTarget.x - ctx.entityX;
        const ndy = this.nudgeTarget.y - ctx.entityY;
        const nudgeVel = scaledNormalize(ndx, ndy, chaseSpeed);
        return {
          movement: { vx: nudgeVel.x, vy: nudgeVel.y, wantsAttack: false },
          stateUpdate,
          logs,
        };
      }
    }

    // --- A* pathfinding toward LKP ---
    const level = this.stuckDetector.escalationLevel;
    const budget = level >= 1 ? ESCALATED_A_STAR_BUDGET : scaledAStarBudget(distToLKP);
    const reversed = level >= 2;
    const result = this.pathHelper.pathfindToward(
      ctx.entityX, ctx.entityY,
      ctx.lastKnownX, ctx.lastKnownY,
      ctx.delta, chaseSpeed,
      () => {
        this.stuckDetector.reset();
        this.nudgeTarget = null;
        this.onChaseArrival(ctx, stateUpdate, logs);
      },
      budget,
      reversed,
    );

    // Measure physical movement since last tick for stuck-detection log messages.
    const movedSinceLastTick = distance(ctx.entityX, ctx.entityY, this.prevEntityX, this.prevEntityY);
    this.prevEntityX = ctx.entityX;
    this.prevEntityY = ctx.entityY;

    // Chase stuck detection — four-stage escalation ladder.
    // Only accumulates when the entity is physically not moving (anchor-based).
    const stuckResult = this.stuckDetector.update(
      ctx.delta,
      ctx.entityX, ctx.entityY,
      distToLKP,
      !!stateUpdate.newState,
    );

    if (stuckResult.triggered === 4) {
      // Stage 4 (terminal): all strategies exhausted — force arrival
      logs.push(`CHASE STUCK: unreachable LKP (${Math.round(ctx.lastKnownX)},${Math.round(ctx.lastKnownY)}) moved=${movedSinceLastTick.toFixed(1)}px — forcing arrival`);
      this.onChaseArrival(ctx, stateUpdate, logs);
    } else if (stuckResult.triggered === 3) {
      // Stage 3: nudge to adjacent non-buffer tile
      const nudge = this.pathHelper.findNudgeTarget(ctx.entityX, ctx.entityY, ctx.lastKnownX, ctx.lastKnownY);
      if (nudge) {
        logs.push(`CHASE STUCK: nudging to (${Math.round(nudge.x)},${Math.round(nudge.y)}) moved=${movedSinceLastTick.toFixed(1)}px`);
        this.nudgeTarget = nudge;
        this.pathHelper.clearPath();
      } else {
        // No valid nudge target — skip straight to terminal
        logs.push(`CHASE STUCK: no nudge target — forcing arrival`);
        this.stuckDetector.reset();
        this.onChaseArrival(ctx, stateUpdate, logs);
      }
    } else if (stuckResult.triggered === 2) {
      // Stage 2: try reverse pathfinding (T→A reversed)
      logs.push(`CHASE STUCK: trying reverse path moved=${movedSinceLastTick.toFixed(1)}px`);
      this.pathHelper.clearPath();
    } else if (stuckResult.triggered === 1) {
      // Stage 1: bump A* budget
      logs.push(`CHASE STUCK: escalating budget moved=${movedSinceLastTick.toFixed(1)}px`);
      this.pathHelper.clearPath();
    }

    return { movement: result, stateUpdate, logs };
  }

  /** Handle arrival at the last-known or predicted-exit position: scan exits, advance chain, or transition to search. */
  private onChaseArrival(ctx: ChaseContext, stateUpdate: ChaseStateUpdate, logs: string[]): void {
    this.searchSystem.recordPosition(ctx.entityX, ctx.entityY);

    if (this.aggroSystem.aggro >= this.config.chaseThreshold) {
      if (!this.searchSystem.hasPredicted) {
        // First arrival at the actual LKP — scan for exits
        logs.push(`ARRIVED at LKP (${Math.round(ctx.lastKnownX)},${Math.round(ctx.lastKnownY)}) aggro=${Math.round(this.aggroSystem.aggro)} → scanning exits`);

        const { beginSearch, logs: exitLogs } = this.searchSystem.predictPlayerExit(
          ctx.entityX, ctx.entityY,
          ctx.playerDirX, ctx.playerDirY,
          this.aggroSystem.aggroHWM,
        );
        logs.push(...exitLogs);

        if (beginSearch) {
          stateUpdate.hasLastKnown = false;
          this.applyEnterSearch(ctx, stateUpdate, logs);
        } else {
          this.applyAdvanceToNextExit(ctx, stateUpdate, logs);
        }
      } else {
        // Arrived at a predicted exit — advance the chain
        logs.push(`ARRIVED at exit (${Math.round(ctx.lastKnownX)},${Math.round(ctx.lastKnownY)}) aggro=${Math.round(this.aggroSystem.aggro)} → next in chain`);
        this.applyAdvanceToNextExit(ctx, stateUpdate, logs);
      }
    } else {
      logs.push(`ARRIVED at LKP (${Math.round(ctx.lastKnownX)},${Math.round(ctx.lastKnownY)}) aggro=${Math.round(this.aggroSystem.aggro)} < chase=${this.config.chaseThreshold} → searching`);
      stateUpdate.hasLastKnown = false;
      this.applyEnterSearch(ctx, stateUpdate, logs);
    }
  }

  /** Pop the next predicted exit and set LKP to it, or transition to search if exhausted. */
  private applyAdvanceToNextExit(ctx: ChaseContext, stateUpdate: ChaseStateUpdate, logs: string[]): void {
    const { exit, log } = this.searchSystem.advanceToPredictedExit();
    logs.push(log);
    if (exit) {
      stateUpdate.lastKnownX = exit.x;
      stateUpdate.lastKnownY = exit.y;
      stateUpdate.hasLastKnown = true;
      stateUpdate.clearPath = true;
    } else {
      stateUpdate.hasLastKnown = false;
      this.applyEnterSearch(ctx, stateUpdate, logs);
    }
  }

  /** Record the intent to transition into search state. The caller initializes the search pattern. */
  private applyEnterSearch(ctx: ChaseContext, stateUpdate: ChaseStateUpdate, logs: string[]): void {
    stateUpdate.newState = 'search';
    stateUpdate.beginSearch = true;
    const searchLogs = this.searchSystem.beginSearch(ctx.entityX, ctx.entityY, ctx.playerDirX, ctx.playerDirY);
    logs.push(...searchLogs);
  }
}
